import { Component, OnInit, OnChanges, Input, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AuthService, DailyReportService, SysNotifyService, TenantSettingsService, GmAssignmentService } from '@org/shared-services';
import { DailyReport, DailyReportViewModel } from '@org/interfaces';
import { forkJoin } from 'rxjs';

@Component({
    selector: 'lib-daily-report-table',
    imports: [CommonModule],
    template: `
   <table class="min-w-full divide-y divide-gray-200">
  <thead class="bg-gray-50">
    <tr>
      <th scope="col" class="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Date</th>
      <th scope="col" class="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Closing Goal - End Of The Day %</th>
      <th scope="col" class="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Closing Goal - End Of The Day$</th>
      <th scope="col" class="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Day</th>
      <th scope="col" class="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Projected Daily</th>
      <th scope="col" class="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Actual Closed</th>
      <th scope="col" class="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Projection Difference</th>
      <th scope="col" class="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Met Projection</th>
      <th scope="col" class="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Daily Variance</th>
      <th scope="col" class="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">% Closed</th>
      <th scope="col" class="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">$ Variance</th>
      <th scope="col" class="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Days Behind</th>
      <th scope="col" class="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Daily GP%</th>
      <th scope="col" class="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Avg Daily $</th>
    </tr>
  </thead>
  <tbody>
    <tr *ngFor="let report of reports" class="hover:bg-gray-50">
      <td class="px-6 py-4 whitespace-nowrap">{{ report.date | date:'EEEE, MMMM d' }}</td>
      <td class="px-6 py-4 whitespace-nowrap">{{ report.closingGoalEndOfDayPercentage }}%</td>
      <td class="px-6 py-4 whitespace-nowrap">{{ report.closingGoalEndOfDay | currency }}</td>
      <td class="px-6 py-4 whitespace-nowrap">{{ report.dayNumber }}</td>
      <td class="px-6 py-4 whitespace-nowrap">{{ report.projectedDaily | currency }}</td>
      <td class="px-6 py-4 whitespace-nowrap">{{ report.actualClosed | currency }}</td>
      <td class="px-6 py-4 whitespace-nowrap">{{ report.projectionDifference | currency }}</td>
      <td class="px-6 py-4 whitespace-nowrap">
        <span [class]="report.metProjection ? 'text-green-600' : 'text-red-600'">
          {{ report.metProjection ? 'Yes' : 'No' }}
        </span>
      </td>
      <td class="px-6 py-4 whitespace-nowrap" [class.text-red-600]="report.dailyVariance < 0">
        {{ report.dailyVariance | currency }}
      </td>
      <td class="px-6 py-4 whitespace-nowrap">{{ report.percentClosed }}%</td>
      <td class="px-6 py-4 whitespace-nowrap" [class.text-red-600]="report.dollarVariance < 0">
        {{ report.dollarVariance | currency }}
      </td>
      <td class="px-6 py-4 whitespace-nowrap">{{ report.daysBehind }}</td>
      <td class="px-6 py-4 whitespace-nowrap">{{ report.dailyGP }}%</td>
      <td class="px-6 py-4 whitespace-nowrap">{{ report.avgDaily | currency }}</td>
    </tr>
  </tbody>
</table>
  `
})
export class DailyReportTableComponent implements OnInit, OnChanges {
  @Input() selectedDate!: string;
  reports: DailyReportViewModel[] = []; 
  loading = false;
  error: string | null = null;
  private tenantId!: string | null;
  currentTenant: string | null = null;
  errorMessage: string = '';
  monthlyBudget: number = 0;
  selectedDateObj: Date = new Date();

  private getTenantContext(): string {
    if (this.authService.currentUserValue && this.authService.currentUserValue.roles && this.authService.currentUserValue.roles.includes('gm')) {
      return this.currentTenant!;
    }
    return this.tenantId!;
  }

  constructor(
    private dailyReportService: DailyReportService, 
    private authService: AuthService,
    private sysNotify: SysNotifyService,
    private tenantSettingsService: TenantSettingsService,
    private gmAssignmentService: GmAssignmentService
  ) {
    this.tenantId = this.authService.currentUserValue?.tenant;
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['selectedDate']) {
      const newDate = changes['selectedDate'].currentValue;
      
      if (!newDate) {
        console.warn('No date selected');
        return;
      }

      try {
        this.selectedDateObj = new Date(newDate);
        
        if (isNaN(this.selectedDateObj.getTime())) {
          console.error('Invalid date:', newDate);
          return;
        }

        
        this.loadMonthlyData();
      } catch (error) {
        console.error('Error processing date:', error);
      }
    }
  }

  ngOnInit() {
    if (this.authService.currentUserValue && this.authService.currentUserValue.roles && this.authService.currentUserValue.roles.includes('gm')) {
      this.gmAssignmentService.getCurrentTenant().subscribe((res: any) => {
        this.currentTenant = res.data.tenant;
        this.initAfterTenant();
      });
    } else {
      this.initAfterTenant();
    }
  }

  private initAfterTenant() {
    if (this.selectedDate) {
      this.loadInitialData();
    }
  }

  private loadInitialData() {
    try {
      this.selectedDateObj = new Date(this.selectedDate);
      
      if (isNaN(this.selectedDateObj.getTime())) {
        console.error('Invalid initial date:', this.selectedDate);
        return;
      }

     
      this.loadMonthlyData();
    } catch (error) {
      console.error('Error processing initial date:', error);
    }
  }

  loadMonthlyData() {
    const month = this.selectedDateObj.getMonth() + 1;
    const year = this.selectedDateObj.getFullYear();

    // Use forkJoin to make parallel calls
    const tenantContext = this.getTenantContext();
    if (tenantContext) {
      forkJoin({
        monthlyReport: this.dailyReportService.getMonthlyReport(tenantContext, year, month),
        settings: this.tenantSettingsService.getTenantSettings()
      }).subscribe({
        next: ({ monthlyReport, settings }) => {
          this.monthlyBudget = settings.monthlyBudget;
          this.handleMonthlyReport(monthlyReport);
        },
        error: (error) => {
          console.error('Error loading reports:', error);
          this.sysNotify.showError('Error loading monthly reports');
        }
      });
    }
  }
  
  private handleMonthlyReport(response: any) {
    const year = this.selectedDateObj.getFullYear();
    const month = this.selectedDateObj.getMonth() + 1;
    const firstDayOfMonth = new Date(year, month - 1, 1);
    const lastDayOfMonth = new Date(year, month, 0);
  
    // Create a map of all weekdays in the month
    const weekdayMap = new Map();
    let weekdayCount = 0;
    for (let d = new Date(firstDayOfMonth); d <= lastDayOfMonth; d.setDate(d.getDate() + 1)) {
        const day = d.getDay();
        if (day !== 0 && day !== 6) { // Skip weekends
            weekdayCount++;
            weekdayMap.set(d.toISOString().split('T')[0], weekdayCount);
        }
    }

   
    const sortedData = [...response.data]
    .filter(report => {
        const date = new Date(report.date);
        const day = date.getDay();
        return day >= 1 && day <= 5; // Monday (1) through Friday (5)
    })
    .sort((a, b) => {
        const dateA = new Date(a.date);
        const dateB = new Date(b.date);
        return dateA.getTime() - dateB.getTime();
    });

    let cumulativeActual = 0;
    let cumulativeGoal = 0;

    this.reports = sortedData.map((report, index) => {
      const date = new Date(report.date);
      const dateStr = new Date(report.date).toISOString().split('T')[0];
      const dayNumber = index + 1; //weekdayMap.get(dateStr);
      cumulativeActual += (report.actualClosed || 0);
      cumulativeGoal += (report.todayGoal || 0);
      const currentMonthlyVariance = cumulativeActual - cumulativeGoal;
      const percentageComplete =  Math.round((dayNumber / (response.totalWorkingDays - 1) ) * 100);
      const metProjectionValue = ( report.actualClosed- report.todayGoal ) >= -2500; //? false : true;
      const actualpercentageComplete = Number(((report.actualClosed / this.monthlyBudget) * 100).toFixed(2));
      const dollarVarianceValue = (Math.round((percentageComplete / 100) * this.monthlyBudget) - report.actualClosed);
      const divideMonthlyBudget = (this.monthlyBudget / response.totalWorkingDays);

      return {
        date: date,
        actualClosed: report.actualClosed,
        tomorrowCloseGoal: report.tomorrowCloseGoal,
        dailyGP: report.dailyGP, // this is the daily GP% set by the GM
        metProjection: metProjectionValue, //report.metProjection,
        todayGoal: report.todayGoal,
        projectedDaily: report.todayGoal, //report.projectedDaily,
        percentageTarget: report.dailyGP, //report.percentageTarget,
        // Calculated fields
        monthlyVariance: currentMonthlyVariance,
        projectionDifference: (report.actualClosed || 0) - (report.todayGoal || 0),
        dailyVariance: ((report.actualClosed || 0) - (Math.round((percentageComplete / 100) * this.monthlyBudget))),
        closingGoalEndOfDayPercentage: percentageComplete,
        closingGoalEndOfDay: Math.round((dayNumber / (response.totalWorkingDays - 1)) * this.monthlyBudget), //Math.round((percentageComplete / 100) * this.monthlyBudget),
        dayNumber: dayNumber ,
        percentClosed: actualpercentageComplete, //report.percentageTarget,
        dollarVariance: dollarVarianceValue, //report.monthlyVariance,
        daysBehind: Number((dollarVarianceValue / divideMonthlyBudget).toFixed(1)),
       // daysBehind: Math.ceil(Number((dollarVarianceValue / divideMonthlyBudget ).toFixed(1))),
        // daysBehind: Math.ceil(Math.abs(currentMonthlyVariance) / (report.projectedDaily || 1)),
        // daysBehind: report.projectedDaily != null && report.projectedDaily > 0 
        //     ? Math.ceil(Math.abs(currentMonthlyVariance) / report.projectedDaily)
        //     : 0,  // For proper calculation, we need to divide by the projected daily amount
        //avgDaily: (report.actualClosed || 0) / new Date(report.date).getDate(),
        avgDaily: cumulativeActual / dayNumber,
      } as DailyReportViewModel; 
    });
  }


  getTotalActualClosed(): number {
    return this.reports.reduce((sum, report) => sum + report.actualClosed, 0);
  }
} 